import { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '@rsos/base-utils/hooks/useDebounce';
import { organizationSelector } from '@rsos/reduxDataStore/src/features/selectors/organizationSelector';
import {
  useGetPaginatedConversationsQuery,
  useDownloadConversationsMutation,
  THIRTY_DAYS,
  type PaginatedConversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import onSAMLrequest from '@rsos/utils/onSAMLrequest';
import { notifyError } from '@rsos/utils/toastUtils';
import useAvailableSessionTypes from '../../../hooks/useAvailableSessionTypes';
import { type AppState } from '../../../types';
import { dayEnd, dayStart } from '../../../utils/helpers';
import styles from './ConversationHistory.module.scss';
import ConversationTable from './ConversationTable/ConversationTable';
import Pagination from './Pagination';
import Toolbar from './Toolbar';

const SUPPORT_SERVICE = 'support';
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

// Stable reference so an empty/skipped query result doesn't change identity
// each render, which would retrigger the checkedList effect indefinitely.
const EMPTY_CONVERSATIONS: PaginatedConversation[] = [];

const ConversationHistory = () => {
  const dispatch = useDispatch();
  const now = useMemo(() => Date.now(), []);
  const availableSessionTypes = useAvailableSessionTypes();

  const [startDate, setStartDate] = useState<Date>(
    () => new Date(now - THIRTY_DAYS),
  );
  const [endDate, setEndDate] = useState<Date>(() => new Date(now));
  const [selectedTypes, setSelectedTypes] = useState<string[]>(() =>
    availableSessionTypes[0]?.value ? [availableSessionTypes[0].value] : [],
  );
  // `searchInput` is what the user sees/types; `searchTerm` is the debounced
  // value actually sent to the server so each keystroke doesn't fire a query.
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [checkedList, setCheckedList] = useState<Set<string>>(new Set());
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const orgName = useSelector((state: AppState) =>
    organizationSelector(state),
  )?.name;
  const psapsDisplayName = useSelector(
    (state: AppState) => state.psaps.currentPSAP?.display_name,
  );
  const isLoadingSAML = useSelector(
    (state: AppState) => state.sinatra.auth.loadingSAML,
  );
  const handleSupportRequest = () =>
    onSAMLrequest(SUPPORT_SERVICE, dispatch, isLoadingSAML);

  const startTime = useMemo(() => dayStart(startDate), [startDate]);
  const endTime = useMemo(() => dayEnd(endDate), [endDate]);
  const sessionTypesParam = selectedTypes.join(',');

  const debouncedSetSearch = useDebounce(
    (value: string) => setSearchTerm(value),
    300,
  );
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      debouncedSetSearch(value);
    },
    [debouncedSetSearch],
  );

  // Any change to a server-side filter invalidates the current page number.
  // Changing the page size also resets to page 1, since the row at the top of
  // the current page would otherwise land mid-page under the new size.
  useEffect(() => {
    setPage(1);
  }, [startTime, endTime, sessionTypesParam, searchTerm, sortDir, pageSize]);

  // Selection persists across pages but is scoped to the active filter set, so
  // clear it whenever the result set itself changes (not on sort/page changes,
  // which only reorder/paginate the same set).
  useEffect(() => {
    setCheckedList(new Set());
  }, [startTime, endTime, sessionTypesParam, searchTerm]);

  const {
    data,
    isFetching: isLoading,
    isError,
  } = useGetPaginatedConversationsQuery(
    {
      orgName,
      startTime,
      endTime,
      sessionTypes: sessionTypesParam,
      // always show active and ended conversations
      // Interagency conversations are always active for example
      status: 'active,ended',
      page,
      limit: pageSize,
      sortDir,
      searchTerm,
    },
    {
      skip: !orgName || selectedTypes.length === 0,
      refetchOnMountOrArgChange: true,
    },
  );

  const conversations = data?.conversations ?? EMPTY_CONVERSATIONS;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const [
    triggerDownload,
    { isLoading: isDownloading, isError: isDownloadError },
  ] = useDownloadConversationsMutation();

  useEffect(() => {
    if (isDownloadError) {
      notifyError('Failed to download conversations.');
    }
  }, [isDownloadError]);

  // The select-all checkbox acts on the current page while preserving rows
  // selected on other pages.
  const isSelectedAllChecked =
    conversations.length > 0 &&
    conversations.every(d => checkedList.has(d.conversation_id));

  const toggleSelectAll = () => {
    setCheckedList(prev => {
      const next = new Set(prev);
      const pageIds = conversations.map(d => d.conversation_id);

      if (isSelectedAllChecked) {
        pageIds.forEach(id => next.delete(id));
      } else {
        pageIds.forEach(id => next.add(id));
      }

      return next;
    });
  };

  const handleClearSelection = () => setCheckedList(new Set());

  const handleCheck = (id: string) => {
    setCheckedList(prev => {
      const next = new Set(prev);

      next.has(id) ? next.delete(id) : next.add(id);

      return next;
    });
  };

  const handleDownload = useCallback(
    (conversationIds: string[]) => {
      triggerDownload({ conversationIds, orgName, psapsDisplayName });
    },
    [orgName, psapsDisplayName, triggerDownload],
  );

  const toggleType = (value: string) => {
    setSelectedTypes(prev => {
      if (prev.includes(value)) {
        return prev.length === 1 ? prev : prev.filter(t => t !== value); // keep at least one selected
      }

      return [...prev, value];
    });
  };

  return (
    <div className={styles.container}>
      <Toolbar
        availableSessionTypes={availableSessionTypes}
        searchTerm={searchInput}
        onSearchChange={handleSearchChange}
        startDate={startDate}
        endDate={endDate}
        now={now}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        selectedTypes={selectedTypes}
        onToggleType={toggleType}
        checkedIds={Array.from(checkedList)}
        onDownload={handleDownload}
        onClearSelection={handleClearSelection}
        isDownloading={isDownloading}
        isLoading={isLoading}
      />

      <p className={styles.description}>
        All conversations are available to download as a CSV file. Please reach
        out to{' '}
        <button className={styles.supportLink} onClick={handleSupportRequest}>
          support
        </button>{' '}
        if you have any questions.
      </p>

      <ConversationTable
        displayList={conversations}
        isSortReversed={sortDir === 'asc'}
        onSort={() => setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'))}
        checkedList={checkedList}
        isSelectedAllChecked={isSelectedAllChecked}
        onToggleSelectAll={toggleSelectAll}
        onCheck={handleCheck}
        onDownload={handleDownload}
        isDownloading={isDownloading}
        isLoading={isLoading}
        isError={isError}
      />

      <Pagination
        page={page}
        totalPages={totalPages}
        total={total}
        shownCount={conversations.length}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageSizeChange={setPageSize}
        onPageChange={p => setPage(Math.min(Math.max(1, p), totalPages))}
        disabled={isLoading}
      />
    </div>
  );
};

export default ConversationHistory;
